import { z } from 'zod';
import type { PresentationCard } from './PresentationCard';
import { VISITOR_ACTIONS, VISITOR_FEATURES } from './HomepageRoom';

export const HOMEPAGE_SESSION_SECONDS = 300;
export const homepageVisitorNameSchema = z.string().trim().min(1, 'Enter your name').max(40, 'Use up to 40 characters').regex(/^[\p{L}\p{M}\p{N} .\-'’]+$/u, 'Use letters, numbers, spaces, apostrophes, or hyphens');
export const HOMEPAGE_FEATURES = VISITOR_FEATURES;
export type HomepageFeature = typeof HOMEPAGE_FEATURES[number];

// Only media URLs and same-site navigation are authored. No scripts or arbitrary AI clicks.
export const homepageMediaUrl = z.string().max(1500).refine(value => {
  if (!value) return true;
  if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true;
  try { return new URL(value).protocol === 'https:'; } catch { return false; }
}, 'Use an HTTPS media URL or a site-relative path');

// Portrait sources are optional so older published editions remain valid.
const responsiveVideoFields = {
  portraitVideoUrl: homepageMediaUrl.default(''),
  portraitVideoPoster: homepageMediaUrl.default(''),
};

export const HOMEPAGE_LANDING_AUDIENCES = ['overview', 'creator', 'sports', 'fam'] as const;
export type HomepageLandingAudience = typeof HOMEPAGE_LANDING_AUDIENCES[number];

export const homepageLandingMessageSchema = z.object({
  label: z.string().trim().min(1).max(60),
  title: z.string().trim().min(1).max(80),
  emphasis: z.string().trim().min(1).max(80),
  description: z.string().trim().min(1).max(400),
  detail: z.string().max(160),
  navDescription: z.string().max(120),
  videoUrl: homepageMediaUrl.default(''),
  videoPoster: homepageMediaUrl.default(''),
  ...responsiveVideoFields,
  videoCaption: z.string().max(200).default(''),
});
export type HomepageLandingMessage = z.infer<typeof homepageLandingMessageSchema>;

export const homepageLandingSceneSchema = homepageLandingMessageSchema.extend({
  // Optional only for saved editions from the original flat landing page.
  audience: z.enum(HOMEPAGE_LANDING_AUDIENCES).optional(),
  messages: z.array(homepageLandingMessageSchema).length(3).optional(),
  // #4616: per-section override of landing.videoPresentsMessaging. Unset follows the
  // landing-wide value, so editions saved before this field render unchanged.
  videoPresentsMessaging: z.boolean().optional(),
});
export type HomepageLandingScene = z.infer<typeof homepageLandingSceneSchema>;

export const DEFAULT_HOMEPAGE_LANDING = {
  eyebrow: 'Your people. Your kind of room.',
  releaseLabel: 'Make something of your time together',
  videoBlur: 1.5,
  overlayOpacity: 0.45,
  videoPresentsMessaging: false,
  ctaTitle: 'Make yourself at home.',
  ctaLabel: 'Meet Tele',
  ctaNote: '5-minute demo · no account · mic optional',
  scenes: [
    {
      audience: 'overview', label: 'Overview', title: 'Your people.', emphasis: 'Something to do together.',
      description: 'Make a show. Get the Sports crew together. Catch up with the Fam. Telegames gives your people a room and an AI host to help make something of the time you share.',
      detail: 'Bring the people. Make it your kind of room.', navDescription: 'Find your kind of room.',
    },
    {
      audience: 'creator', label: 'Creator', title: 'You host.', emphasis: 'Tele produces.',
      description: 'Bring your idea and your point of view. Your AI producer helps prepare the show, work the room, and bring your audience into the action.',
      detail: 'Your voice. Your audience. An AI on staff.', navDescription: 'Produce. Publish. Bring people in.',
      messages: [
        { label: 'Your AI producer', title: 'Bring the idea.', emphasis: 'Tele brings the prep.', description: 'Research the stories, prepare the segments, and cue the next moment with your AI producer. You lead the conversation and make the show yours.', detail: 'Research. Brief. Cue. Hand it back.', navDescription: 'A producer in your room.' },
        { label: 'Record & livestream', title: 'Record it. Stream it.', emphasis: 'Keep it.', description: 'Stream to YouTube and Twitch from the room you’re already hosting. Keep the recording and give your next audience a reason to show up.', detail: 'Your room. Your recording. Your destinations.', navDescription: 'Take the show to your audience.' },
        { label: 'Audience participation', title: 'React. Direct.', emphasis: 'Appear.', description: 'Let viewers throw reactions, ask questions, and vote. When they have something to add, they can raise a hand for you to invite them onto the stage.', detail: 'Three ways to join the action. You decide who takes the stage.', navDescription: 'Turn viewers into participants.' },
      ],
    },
    {
      audience: 'sports', label: 'Sports crew', title: 'Your team. Your crew.', emphasis: 'Your commentary.',
      description: 'Different couches. Same big game. Bring the Sports crew into one room for the calls, the arguments, and the celebrations, with an AI sports junkie in the mix.',
      detail: 'Watch on your own screen. Keep the crew here.', navDescription: 'Game-day company, wherever you are.',
      messages: [
        { label: 'In sync with the game', title: 'Stay with', emphasis: 'the action.', description: 'Follow the college-football watch-along with commentary and game context timed to the action you’re watching. Keep your crew in the conversation through every big moment.', detail: 'Your game on your screen. The watch-along in your room.', navDescription: 'Keep the room with the action.' },
        { label: 'Live data overlays', title: 'Big opinions.', emphasis: 'Bring the numbers.', description: 'Put live college-football stats and game data on screen. Give the crew something to point at when the debate needs more than a gut feeling.', detail: 'Scores, context, and receipts for the argument.', navDescription: 'Settle it with the numbers.' },
        { label: 'Your AI sports junkie', title: 'A sports junkie.', emphasis: 'On your mic.', description: 'Give your game a voice with an AI announcer bringing play-by-play, college-football context, and opinions worth arguing with. Ask a question or jump in with your own call.', detail: 'Play-by-play. Context. A little friendly rivalry.', navDescription: 'Your crew’s own commentator.' },
      ],
    },
    {
      audience: 'fam', label: 'Fam', title: 'Different places.', emphasis: 'Same Fam.',
      description: 'Miss your people? Bring friends and family into one room. Catch up, set the kickback vibe, and turn “we should hang out” into a night worth doing again.',
      detail: 'Catch up. Play a round. Stay for another.', navDescription: 'Your people. Something to do together.',
      messages: [
        { label: 'One text away', title: 'Send a text.', emphasis: 'Get the Fam together.', description: 'Drop your room link in the group chat. Bring your friends, siblings, cousins, or old roommates together and pick up where you left off.', detail: 'Different cities. Familiar faces.', navDescription: 'A room link for the group chat.' },
        { label: 'Set the kickback vibe', title: 'Your kickback.', emphasis: 'Your kind of energy.', description: 'Give your AI host a personality and mood that fit your people. Keep it laid-back, bring the banter, or turn up the game-show energy as the night unfolds.', detail: 'Pick the character. Set the tone. Make it yours.', navDescription: 'Have the AI match your crew.' },
        { label: 'Casual Games Buddy', title: 'Keep the party', emphasis: 'going.', description: 'Let your Casual Games Buddy explain the rules, run the rounds, and celebrate the winner. Play trivia, trade ridiculous answers, and catch up between laughs.', detail: 'One more round. One more story. Another reason to meet up.', navDescription: 'Something fun to do while you catch up.' },
      ],
    },
  ].map(scene => homepageLandingSceneSchema.parse(scene)),
};

export const homepageLandingSchema = z.object({
  eyebrow: z.string().trim().min(1).max(120),
  releaseLabel: z.string().max(120),
  videoBlur: z.number().min(0).max(8).default(1.5),
  overlayOpacity: z.number().min(0).max(0.85).default(0.45),
  // Motion-graphic clips carry their own messaging: hide the overview copy and
  // the three message buttons, and play the video clean (no blur or shade).
  // The authored copy is kept, so switching back restores the overlay.
  // Landing-wide fallback; each section can override it (#4616).
  videoPresentsMessaging: z.boolean().default(false),
  ctaTitle: z.string().trim().min(1).max(100),
  ctaLabel: z.string().trim().min(1).max(60),
  ctaNote: z.string().max(160),
  scenes: z.array(homepageLandingSceneSchema).min(3).max(4),
}).superRefine((landing, ctx) => {
  if (!landing.scenes.some(scene => scene.audience || scene.messages)) return;
  if (landing.scenes.length !== 4 || landing.scenes.some((scene, index) => scene.audience !== HOMEPAGE_LANDING_AUDIENCES[index])) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scenes'], message: 'Include Overview, Creator, Sports crew, and Fam in that order' });
  }
  landing.scenes.forEach((scene, index) => {
    if ((scene.audience === 'overview' && scene.messages) || (scene.audience !== 'overview' && !scene.messages)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scenes', index, 'messages'], message: 'Use three messages for each audience and no sub-messages for Overview' });
    }
  });
});
export type HomepageLanding = z.infer<typeof homepageLandingSchema>;

/** Whether this section's clip carries its own messaging (#4616). */
export function sceneVideoPresentsMessaging(landing: Pick<HomepageLanding, 'videoPresentsMessaging'>, scene: Pick<HomepageLandingScene, 'videoPresentsMessaging'>): boolean {
  return scene.videoPresentsMessaging ?? landing.videoPresentsMessaging;
}

/** Reorganize older editions without dropping authored copy, clips, or posters.
 * Reading never writes the stored edition; edits still use draft/preview/publish.
 */
export function organizeHomepageLanding(landing: HomepageLanding): HomepageLanding {
  if (landing.scenes.every(scene => scene.audience)) return landing;
  const scenes = DEFAULT_HOMEPAGE_LANDING.scenes.map(scene => homepageLandingSceneSchema.parse(scene));
  const keep = (section: number, message: number, legacy: HomepageLandingScene) => {
    scenes[section].messages![message] = homepageLandingMessageSchema.parse(legacy);
  };
  // Original three messages: production, participation, and publishing.
  keep(1, 0, landing.scenes[0]);
  keep(1, 1, landing.scenes[2]);
  if (landing.scenes.length === 3) keep(1, 2, landing.scenes[1]);
  // Legacy four-message layout: production, Sports crew, publishing, Fam.
  else { keep(2, 0, landing.scenes[1]); keep(3, 0, landing.scenes[3]); }
  return { ...landing, scenes };
}

export const homepageCardSchema = z.object({
  layout: z.enum(['bullets', 'comparison', 'stat_card', 'quote', 'quiz', 'timeline']),
  title: z.string().min(1).max(140),
  items: z.array(z.string().max(250)).max(5).optional(),
  comparisonColumns: z.tuple([z.string().max(80), z.string().max(80)]).optional(),
  comparisonRows: z.array(z.object({ left: z.string().max(180), right: z.string().max(180) })).max(5).optional(),
  statValue: z.string().max(60).optional(),
  statLabel: z.string().max(180).optional(),
  quoteText: z.string().max(500).optional(),
  quoteAttribution: z.string().max(100).optional(),
  quizQuestion: z.string().max(200).optional(),
  quizOptions: z.array(z.string().max(120)).min(2).max(4).optional(),
  quizCorrectIndex: z.number().int().min(0).max(3).optional(),
  quizExplanation: z.string().max(300).optional(),
  timelineEvents: z.array(z.object({ date: z.string().max(60), label: z.string().max(120), detail: z.string().max(180).optional() })).max(5).optional(),
  sourceAttribution: z.string().max(300).optional(),
}).superRefine((card, ctx) => {
  const missing = (field: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Add content for the selected presentation layout' });
  if (card.layout === 'bullets' && !card.items?.some(item => item.trim())) missing('items');
  if (card.layout === 'comparison' && (!card.comparisonColumns?.every(Boolean) || !card.comparisonRows?.length)) missing('comparisonRows');
  if (card.layout === 'stat_card' && !card.statValue?.trim()) missing('statValue');
  if (card.layout === 'quote' && !card.quoteText?.trim()) missing('quoteText');
  if (card.layout === 'timeline' && !card.timelineEvents?.length) missing('timelineEvents');
  if (card.layout === 'quiz' && (!card.quizQuestion?.trim() || !card.quizOptions?.length || card.quizCorrectIndex === undefined || card.quizCorrectIndex >= card.quizOptions.length)) missing('quizOptions');
});

export const homepageChapterSchema = z.object({
  feature: z.enum(HOMEPAGE_FEATURES),
  title: z.string().min(1).max(100),
  narration: z.string().min(1).max(1000),
  voiceDirective: z.string().min(1).max(160),
  // Retained when reading previously published editions; never drives the room.
  seconds: z.number().int().min(10).max(30).default(18),
  openingLine: z.string().max(220).default(''),
  followUp: z.string().max(220).default('What would you like to try?'),
  fallback: z.string().max(220).default('Let’s try something else while that is unavailable.'),
  allowedActions: z.array(z.enum(VISITOR_ACTIONS)).max(VISITOR_ACTIONS.length).optional(),
  card: homepageCardSchema,
  videoUrl: homepageMediaUrl.default(''),
  videoPoster: homepageMediaUrl.default(''),
  ...responsiveVideoFields,
  videoCaption: z.string().max(300).default(''),
});

export const homepageExperienceSchema = z.object({
  version: z.union([z.literal(1), z.literal(2)]),
  /** Retired with companions (#4632 phase 3d); kept so published editions still parse. */
  companionId: z.string().max(100).default(''),
  /** The visitor cast member the room starts on: a character id, like `drake` (D10). Empty: the first. */
  characterId: z.string().max(100).default(''),
  featuredExperience: z.enum(HOMEPAGE_FEATURES).default('programs'),
  // Kept for published editions; the beginner welcome always leads with the producer.
  openingExperience: z.enum(['media', 'programs', 'activities', 'audience']).default('programs'),
  title: z.string().min(1).max(160),
  description: z.string().min(1).max(400),
  guideName: z.string().min(1).max(40),
  welcome: z.string().min(1).max(160),
  closingTitle: z.string().min(1).max(120),
  closingDescription: z.string().min(1).max(400),
  ctaLabel: z.string().min(1).max(60),
  landing: homepageLandingSchema.default(DEFAULT_HOMEPAGE_LANDING),
  commercial: z.object({ ...responsiveVideoFields, title: z.string().min(1).max(140), highlights: z.array(z.string().min(1).max(200)).min(1).max(4), videoUrl: homepageMediaUrl, poster: homepageMediaUrl, caption: z.string().max(300), seconds: z.number().int().min(5).max(30) }),
  // Signup destination is intentionally fixed by the product, not supplied by the model.
  chapters: z.array(homepageChapterSchema).length(10),
}).superRefine((value, ctx) => {
  if (new Set(value.chapters.map(chapter => chapter.feature)).size !== 10) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['chapters'], message: 'Include each of the ten room features exactly once' });
  }

});

export type HomepageExperience = z.infer<typeof homepageExperienceSchema>;
export type HomepageChapter = HomepageExperience['chapters'][number];
export function homepageVisitorWelcome(name: string, guideName: string): string {
  return `Welcome, ${name}! I’m ${guideName}, your AI producer and master of ceremonies. This is a demo room with a small sample of Telegames. I’m here to shape the best experience for you and your friends as we go. Your own room lets you enjoy it together, keep recordings, or live stream to an audience on YouTube and other platforms. You can just watch, or jump in anytime.`;
}
export type HomepageVisitPhase = { kind: 'spotlight' } | { kind: 'tour'; index: number } | { kind: 'conversation' } | { kind: 'expired' };

export function homepageVisitPhase(elapsedSeconds: number, _content?: HomepageExperience): HomepageVisitPhase {
  if (elapsedSeconds >= HOMEPAGE_SESSION_SECONDS) return { kind: 'expired' };
  return { kind: 'conversation' };
}

export function homepagePresentationCard(chapter: HomepageChapter): PresentationCard {
  return { ...chapter.card, id: `homepage-${chapter.feature}`, priority: 1, speakingCue: chapter.narration };
}

export const DEFAULT_HOMEPAGE_EXPERIENCE: HomepageExperience = homepageExperienceSchema.parse({
  version: 2,
  companionId: '',
  characterId: '',
  featuredExperience: 'programs',
  openingExperience: 'programs',
  title: 'Your people. Something to do together. | Telegames',
  description: 'Make a show, bring the Sports crew together, or catch up with the Fam. Your people, a shared room, and an AI host to help make it yours.',
  guideName: 'Tele',
  welcome: 'Welcome them by name. Explain this is a light version of a Telegames room. Be their AI producer and master of ceremonies; show one useful thing at a time.',
  closingTitle: 'Your show starts with a room.',
  closingDescription: 'Bring your topic, your point of view, and your audience. Your AI is on staff from the first episode.',
  ctaLabel: 'Create your room',
  commercial: { title: 'Your next episode, prepared before you arrive.', highlights: ['Schedule a recurring show.', 'Your producer assembles researched stories, presentation cards, and b-roll.', 'Choose the edition. Show up and host.'], videoUrl: '', poster: '', caption: 'New in your room · Scheduled programs', seconds: 15 },
  chapters: [
    {
      feature: 'programs', title: 'Your show, ready before you arrive', seconds: 18,
      narration: 'Your show can be ready before you arrive. I’m opening a prepared edition: researched stories, presentation cards, and b-roll. Schedule a recurring show, choose the edition you want, and put it on stage. You show up and host.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Show me the prepared program',
      card: { layout: 'timeline', title: 'Tonight: turn viewers into participants', timelineEvents: [
        { date: 'Opening', label: 'What makes you stay for a live show?' },
        { date: 'Segment 1', label: 'Give the audience a say' },
        { date: 'Segment 2', label: 'Bring a viewer onto the stage' },
        { date: 'Close', label: 'Ask them what comes next' },
      ], sourceAttribution: 'Example prepared edition' }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'research', title: 'Research that joins the conversation', seconds: 18,
      narration: 'Research joins the conversation. X tells you what people are saying; prediction markets show what they will bet on. Web sources and curated knowledge add context. In your own room, ask for the source and put the answer on stage.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'What can the room research?',
      card: { layout: 'bullets', title: 'Bring the source onto the stage', items: ['X: what people are saying', 'Polymarket: what people are willing to bet', 'Hacker News: what a technical audience engaged with', 'Web: what has been written', 'Curated knowledge: the context your show needs'] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'presentations', title: 'Put the point on screen', seconds: 18,
      narration: 'Let’s put the point on screen. Here’s a comparison for our show about audience participation. This is the room’s presentation surface: cards, quotes, charts, and questions, cued while you keep the conversation going.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Put the comparison on stage',
      card: { layout: 'comparison', title: 'Watching becomes participating', comparisonColumns: ['Watching', 'In your room'], comparisonRows: [{ left: 'React somewhere in a chat feed', right: 'Aim a reaction at the stage' }, { left: 'Hope the host sees a question', right: 'Put a question or poll on screen' }, { left: 'Stay behind the screen', right: 'Raise a hand to join the stage' }] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'media', title: 'Cue it with your voice', seconds: 18,
      narration: 'You can run the room by talking. Say play the clip, pause the video, or next slide. Your videos and presentation share the stage. You stay with the conversation while the room takes the cue.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Play the clip',
      card: { layout: 'bullets', title: 'You speak. The room takes the cue.', items: ['“Play the clip.”', '“Pause the video.”', '“Next slide.”', 'Keep your attention on the conversation.'] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'direction', title: 'A second voice with a brief', seconds: 18,
      narration: 'The AI is on staff. Give it a brief: challenge vague claims, sharpen the question, then hand the conversation back. It can keep the pace and work the room. You decide how your second voice helps you host.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Challenge the idea that bigger audiences make better shows',
      card: { layout: 'quote', title: 'You run the show.', quoteText: 'Hand off rather than answer. Press rather than ratify. Take the dead air.', quoteAttribution: 'An example direction sheet' }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'cast', title: 'Make the cast yours', seconds: 18,
      narration: 'Make the cast yours. Choose a character, give it a name, preview a voice, and write the direction. The same character can be a curious interviewer or a skeptical producer. You can appear behind a voice-driven avatar too.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Act as a skeptical producer',
      card: { layout: 'bullets', title: 'A cast you can direct', items: ['Choose a face and give it a name', 'Hear the voice before choosing it', 'Write the personality and performance brief', 'Tell it what to remember about your room', 'Appear as a voice-driven avatar yourself'] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'activities', title: 'It runs the round. You make the show.', seconds: 18,
      narration: 'The AI can run a round, read the prompts, narrate reveals, and keep score. In SociaPath, a human player judges the winner. Start a playable solo trivia round and respond to the visitor’s answer. The machine does the work; the people make the show.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Show me an activity',
      card: { layout: 'quiz', title: 'A quick round with Tele', quizQuestion: 'Which animal has three hearts?', quizOptions: ['An octopus', 'A dolphin', 'An elephant'], quizCorrectIndex: 0, quizExplanation: 'An octopus has two hearts pumping blood to its gills and one pumping it to the rest of its body.' }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'reactions', title: 'Tomatoes land where you aim', seconds: 18,
      narration: 'A tomato lands where the audience aims. Select a reaction and tap the part of the stage that earned it. In a live room, everyone sees it land in the same place, including the broadcast audience. Invite them to try it immediately.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Let me throw a tomato',
      card: { layout: 'stat_card', title: 'Heckle the show. It feels it.', statValue: 'Your aim.', statLabel: 'Your reaction lands on the part of the stage that earned it.' }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'audience', title: 'React. Direct. Appear.', seconds: 18,
      narration: 'React, direct, appear. People can watch, react, ask questions, and vote from a public link without an account. To join the stage, a viewer signs in and raises a hand. You approve the guest and stay in control.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'How does a viewer get on stage?',
      card: { layout: 'timeline', title: 'From viewer to your next guest', timelineEvents: [{ date: 'React', label: 'Throw a reaction. No account needed.' }, { date: 'Direct', label: 'Ask a question or vote in a poll.' }, { date: 'Appear', label: 'Sign in, raise a hand, and join with host approval.' }] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
    {
      feature: 'broadcast', title: 'Record and reach your audience', seconds: 18,
      narration: 'Record and Stream lives inside your room. Send your show to YouTube, Twitch, and custom RTMP destinations. Recording has no extra charge. These are preview controls; your own room connects the accounts and starts the broadcast.',
      openingLine: '', followUp: 'What would you like to try?', fallback: 'Let’s try something else while that loads.',
      voiceDirective: 'Show me Record and Stream',
      card: { layout: 'bullets', title: 'Your show. Your destinations.', items: ['Record inside the room you are hosting', 'Simulcast to YouTube, Twitch, and custom RTMP', 'See the live status and stream health', 'Keep the recording', 'You see your room rate before the meter starts'] }, videoUrl: '', videoPoster: '', videoCaption: '',
    },
  ],
});

export function resolveHomepageExperience(value: unknown): HomepageExperience {
  const parsed = homepageExperienceSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_HOMEPAGE_EXPERIENCE;
}
